import { observable, observe, runInAction } from "mobx"

import { formatTemplate } from "@/lib/formatTemplate.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { compareStringAsInteger, recordToMap } from "@/lib/std.ts"

import type { ListModel } from "@/components/Chat/LoadMoreButton.tsx"
import type { BatchInfo, LLMLogEntry, MessagesSorting } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Persistence } from "@/lib/persistence.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"


const log = getLogger("LLMLogChatModel")

export interface LLMLogChatModel extends ListModel {
  logs: IObservableValue<LLMLogModel[]>
  contextLogs: IObservableValue<LLMLogModel[]>
  hasNext: IObservableValue<boolean>
  hasPrev: IObservableValue<boolean>
  hasContextPrev: IObservableValue<boolean>
  isLoadingNext: IObservableValue<boolean>
  isLoadingPrev: IObservableValue<boolean>
  isLoadingContextPrev: IObservableValue<boolean>
  isLoadingInitially: IObservableValue<boolean>
  isReady: IObservableValue<boolean>

  loadNext(): void

  loadPrev(): void

  loadContextPrev(): void

  terminate(): void
}

export type LLMLogModel = LLMLogEntry & {
  collapsed: IObservableValue<boolean>
  formattedMessage: string
  shortFormattedMessage: string

  toggleCollapsed(): void
}

export function createLLMLogChatModel(
  taskId: string,
  queries: Queries,
  persistence: Persistence,
  pageId: string | null,
  batchSize: number = 20,
): LLMLogChatModelImpl {
  return new LLMLogChatModelImpl(taskId, queries, persistence, pageId, batchSize)
}

const COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY = "collapse-system-and-context-messages"
const DISABLE_AUTOSCROLL_KEY = "disable-autoscroll"

type LoadType = 
  | "update-recent" 
  | "load-next" 
  | "load-prev"
  | "update-context-recent"
  | "load-context-prev"

class LLMLogChatModelImpl extends Model implements LLMLogChatModel {
  private readonly logComparator: (a: LLMLogEntry, b: LLMLogEntry) => number = (a, b) => {
    return compareStringAsInteger(a.longId, b.longId)
  }

  // Regular logs (isContext = false)
  private readonly rawLogs = new Map<string, LLMLogEntry>()
  
  // Context logs (isContext = true)
  private readonly rawContextLogs = new Map<string, LLMLogEntry>()
  
  // Unified queue
  private readonly updateQueue: LoadType[] = []
  private isProcessing: boolean = false

  logs = observable.box<LLMLogModel[]>([])
  contextLogs = observable.box<LLMLogModel[]>([])
  hasNext = observable.box(false)
  hasPrev = observable.box(true)
  hasContextPrev = observable.box(true)
  isLoadingNext = observable.box(false)
  isLoadingPrev = observable.box(false)
  isLoadingContextPrev = observable.box(false)
  isLoadingInitially = observable.box(true)
  isReady = observable.box(false)
  collapseSystemAndContextMessages = observable.box(true)
  disableAutoscroll = observable.box(false)

  constructor(private readonly taskId: string, private readonly queries: Queries, private readonly persistence: Persistence, private readonly pageId: string | null, private readonly batchSize: number = 20) {
    super("LLMLogChatModelImpl")

    runInAction(() => {
      this.collapseSystemAndContextMessages.set(persistence.getItem(COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY) === null || persistence.getItem(COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY) === "true")
      this.disableAutoscroll.set(persistence.getItem(DISABLE_AUTOSCROLL_KEY) === "true")
    })

    this.lifetime.add(observe(this.collapseSystemAndContextMessages, (value) => {
      this.persistence.setItem(COLLAPSE_SYSTEM_AND_CONTEXT_MESSAGES_KEY, value.newValue.toString())
      this.updateLogsList()
      this.updateContextLogsList()
    }), "LLMLogChatModelImpl:collapseSystemAndContextMessages")

    this.lifetime.add(observe(this.disableAutoscroll, (value) => {
      this.persistence.setItem(DISABLE_AUTOSCROLL_KEY, value.newValue.toString())
    }), "LLMLogChatModelImpl:disableAutoscroll")

    void this.startQueueProcessing()
    void this.listenForUpdates()
  }

  readonly loadNext = () => {
    this.scheduleUpdate("load-next")
  }

  readonly loadPrev = () => {
    this.scheduleUpdate("load-prev")
  }

  readonly loadContextPrev = () => {
    this.scheduleUpdate("load-context-prev")
  }

  private listenForUpdates() {
    const updatesChannel = this.queries.llmLogUpdates(this.taskId, this.pageId)
    this.scheduleUpdate("update-recent")
    this.scheduleUpdate("update-context-recent")

    this.lifetime.add(updatesChannel.subscribe(() => {
      if (!this.hasNext.get()) {
        this.scheduleUpdate("update-recent")
      }
      this.scheduleUpdate("update-context-recent")
    }), "LLMLogChatModelImpl:updatesChannel")
  }

  terminate() {
    super.terminate()
    this.updateQueue.length = 0
  }

  private async startQueueProcessing() {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true

    while (this.updateQueue.length > 0) {
      try {
        const request = this.updateQueue.shift()!
        log.debug("Process event", request)
        
        switch (request) {
          case "load-next":
            if (!this.isLoadingNext.get() && this.hasNext.get()) {
              await this.loadRegular(request)
            }
            break
          case "load-prev":
            if (!this.isLoadingPrev.get() && this.hasPrev.get()) {
              await this.loadRegular(request)
            }
            break
          case "update-recent":
            await this.loadRegular(request)
            break
          case "load-context-prev":
            if (!this.isLoadingContextPrev.get() && this.hasContextPrev.get()) {
              await this.loadContext(request)
            }
            break
          case "update-context-recent":
            await this.loadContext(request)
            break
        }
      } catch (ex) {
        log.error("Error during queue processing:", ex)
      }
    }

    this.isProcessing = false
  }

  private scheduleUpdate(update: LoadType) {
    if (this.updateQueue.includes(update)) {
      return
    }

    this.updateQueue.push(update)

    if (!this.isProcessing) {
      void this.startQueueProcessing()
    }
  }

  private getLatestLogId(): string | null {
    if (this.rawLogs.size == 0) {
      return null
    }

    return [...this.rawLogs.values()].reduce((max, current) =>
      compareStringAsInteger(current.longId, max.longId) > 0 ? current : max
    ).id
  }

  private getOldestLogId(): string | null {
    if (this.rawLogs.size == 0) {
      return null
    }

    return [...this.rawLogs.values()].reduce((min, current) =>
      compareStringAsInteger(current.longId, min.longId) < 0 ? current : min
    ).id
  }

  private getOldestContextLogId(): string | null {
    if (this.rawContextLogs.size == 0) {
      return null
    }

    return [...this.rawContextLogs.values()].reduce((min, current) =>
      compareStringAsInteger(current.longId, min.longId) < 0 ? current : min
    ).id
  }

  private consumeBatch(batch: LLMLogEntry[]) {
    for (const item of batch) {
      this.rawLogs.set(item.id, item)
    }

    this.updateLogsList()
  }

  private consumeContextBatch(batch: LLMLogEntry[]) {
    for (const item of batch) {
      this.rawContextLogs.set(item.id, item)
    }

    this.updateContextLogsList()
  }

  private resetState() {
    runInAction(() => {
      this.rawLogs.clear()
      this.hasNext.set(false)
      this.hasPrev.set(true)
    })
  }

  private resetContextState() {
    runInAction(() => {
      this.rawContextLogs.clear()
      this.hasContextPrev.set(true)
    })
  }

  private async getBatch(offset: string | null, sorting: MessagesSorting, type: "update-recent" | "load-next" | "load-prev"): Promise<LLMLogEntry[]> {
    const batchInfo: BatchInfo = { offset, batchSize: this.batchSize }
    const batch = (await this.queries.llmLog(this.taskId, this.pageId, batchInfo, sorting, false)).data
    if (batch.length < this.batchSize) {
      if (sorting === "FromNewestToOldest") {
        runInAction(() => this.hasPrev.set(false))
      } else if (sorting === "FromOldestToNewest") {
        runInAction(() => this.hasNext.set(false))
      }
    } else if (type === "update-recent") {
      let newMessagesCount = 0
      for (const message of batch) {
        if (!this.rawLogs.has(message.id)) {
          newMessagesCount++
        }
      }
      if (newMessagesCount >= this.batchSize) {
        this.resetState()
      }
    }
    return batch
  }

  private async getContextBatch(offset: string | null, sorting: MessagesSorting, type: "update-context-recent" | "load-context-prev"): Promise<LLMLogEntry[]> {
    const batchInfo: BatchInfo = { offset, batchSize: this.batchSize }
    const batch = (await this.queries.llmLog(this.taskId, this.pageId, batchInfo, sorting, true)).data
    if (batch.length < this.batchSize) {
      if (sorting === "FromNewestToOldest") {
        runInAction(() => this.hasContextPrev.set(false))
      }
    } else if (type === "update-context-recent") {
      let newMessagesCount = 0
      for (const message of batch) {
        if (!this.rawContextLogs.has(message.id)) {
          newMessagesCount++
        }
      }
      if (newMessagesCount >= this.batchSize) {
        this.resetContextState()
      }
    }
    return batch
  }

  private async loadRegular(type: "update-recent" | "load-next" | "load-prev") {
    let sorting: MessagesSorting | null = null
    let offset: string | null = null

    runInAction(() => {
      this.isLoadingNext.set(type === "load-next")
      this.isLoadingPrev.set(type === "load-prev")
    })

    try {
      if (type === "load-prev") {
        sorting = "FromNewestToOldest"
        offset = this.getOldestLogId()
      } else if (type === "load-next") {
        sorting = "FromOldestToNewest"
        offset = this.getLatestLogId()
      } else if (type === "update-recent") {
        sorting = "FromNewestToOldest"
        offset = null
      } else {
        return
      }

      this.consumeBatch(await this.getBatch(offset, sorting, type))
    } finally {
      runInAction(() => {
        this.isLoadingPrev.set(false)
        this.isLoadingNext.set(false)
        this.isLoadingInitially.set(false)
        this.isReady.set(true)
      })
    }
  }

  private async loadContext(type: "update-context-recent" | "load-context-prev") {
    let sorting: MessagesSorting | null = null
    let offset: string | null = null

    runInAction(() => {
      this.isLoadingContextPrev.set(type === "load-context-prev")
    })

    try {
      if (type === "load-context-prev") {
        sorting = "FromNewestToOldest"
        offset = this.getOldestContextLogId()
      } else if (type === "update-context-recent") {
        sorting = "FromNewestToOldest"
        offset = null
      } else {
        return
      }

      this.consumeContextBatch(await this.getContextBatch(offset, sorting, type))
    } finally {
      runInAction(() => {
        this.isLoadingContextPrev.set(false)
      })
    }
  }

  private updateLogsList() {
    const logs: LLMLogModel[] = []
    for (const entry of this.rawLogs.values()) {
      logs.push(this.createLogModel(entry))
    }

    logs.sort(this.logComparator)
    log.debug(`Total logs count: ${logs.length}`)

    runInAction(() => this.logs.set(logs))
  }

  private updateContextLogsList() {
    const logs: LLMLogModel[] = []
    for (const entry of this.rawContextLogs.values()) {
      logs.push(this.createLogModel(entry))
    }

    logs.sort(this.logComparator)
    log.debug(`Total context logs count: ${logs.length}`)

    runInAction(() => this.contextLogs.set(logs))
  }

  private createLogModel(entry: LLMLogEntry): LLMLogModel {
    const formattedMessage = formatTemplate(entry.message, recordToMap(entry.params))
    const lines = formattedMessage.split("\n")
    const shortFormattedMessage = lines.length > 3 ? lines.slice(0, 3).join("\n") : formattedMessage

    const entryModel: LLMLogModel = {
      ...entry,
      collapsed: observable.box(
        this.collapseSystemAndContextMessages.get() && (entry.role == "System" || entry.isContext == true)
      ),
      formattedMessage: formattedMessage,
      shortFormattedMessage: shortFormattedMessage,
      toggleCollapsed: () => {
        runInAction(() => entryModel.collapsed.set(!entryModel.collapsed.get()))
      }
    }

    return entryModel
  }
}
